import { Link } from "react-router-dom";
import BlogPostLayout from "./BlogPostLayout";
import { getPostBySlug } from "../../lib/blog";

const FAQ = [
  {
    q: "Quel est le meilleur moment pour retirer un nid de chenilles processionnaires du pin ?",
    a: "De l'automne à la fin de l'hiver, lorsque les nids sont bien visibles et que les chenilles s'y trouvent encore. Une fois les processions commencées, il est trop tard pour l'échenillage : il faut alors intercepter les chenilles au tronc ou au sol.",
  },
  {
    q: "Les poils urticants restent-ils dangereux après le départ des chenilles ?",
    a: "Oui. Les poils restent urticants longtemps dans les anciens nids, au sol et dans les mues. Ne manipulez pas un vieux nid à mains nues et évitez de tondre ou de souffler les feuilles sous un arbre très infesté sans protection.",
  },
  {
    q: "Les éco-pièges fonctionnent-ils sur les chênes ?",
    a: "Les colliers sont conçus pour la processionnaire du pin, qui descend le long du tronc pour s'enfouir. La processionnaire du chêne vit et se nymphose sur l'arbre : on la traite plutôt par retrait des nids au printemps et en été, avec un équipement adapté.",
  },
  {
    q: "Faut-il traiter tous les ans ?",
    a: "Dans les secteurs exposés, la pression revient souvent d'une année sur l'autre. Une surveillance annuelle, des éco-pièges reposés chaque hiver et des nichoirs à mésanges permettent généralement de réduire les interventions lourdes.",
  },
];

export default function ChenillesQuandCommentCreuse() {
  const post = getPostBySlug("chenilles-processionnaires-quand-comment-s-en-debarrasser-creuse");
  return (
    <BlogPostLayout post={post} faq={FAQ}>
      <p className="article-lead">
        En Creuse, les chenilles processionnaires du pin et du chêne reviennent chaque année. La bonne nouvelle : des
        solutions efficaces existent. La moins bonne : <strong>chacune a sa saison</strong>, et une méthode appliquée trop
        tôt ou trop tard ne sert presque à rien. Voici le calendrier à suivre pour agir au bon moment, sans prendre de
        risques.
      </p>

      <h2>Pourquoi il faut s'en occuper</h2>
      <p>
        Les chenilles portent des milliers de poils microscopiques qui se détachent au moindre stress et se dispersent
        avec le vent. Ils provoquent des démangeaisons, des irritations des yeux et parfois des réactions respiratoires.
        Pour les chiens et les chats, un contact avec la langue est une urgence vétérinaire. Depuis 2022, les
        processionnaires du pin et du chêne figurent d'ailleurs dans la liste des espèces dont la prolifération est
        nuisible à la santé humaine, dans le Code de la santé publique.
      </p>
      <p>
        Pour les reconnaître et comprendre les risques en détail, consultez aussi notre article{" "}
        <Link to="/conseils/chenilles-processionnaires-danger-traitement">chenilles processionnaires : danger et traitement</Link>.
      </p>

      <h2>Pin ou chêne : deux calendriers différents</h2>
      <h3>La processionnaire du pin</h3>
      <p>
        Elle tisse en automne de gros nids blancs et soyeux à l'extrémité des branches de pins, parfois de cèdres. Les
        chenilles y passent l'hiver, puis descendent en file indienne, le plus souvent entre janvier et avril selon la
        douceur de la saison, pour s'enterrer au pied de l'arbre.
      </p>
      <h3>La processionnaire du chêne</h3>
      <p>
        Elle est active plus tard : les chenilles apparaissent au printemps et forment des amas sur le tronc et les
        grosses branches, surtout de mai à juillet. Ses poils sont tout aussi urticants, et ses anciens nids restent
        dangereux longtemps après.
      </p>

      <h2>Le calendrier de lutte, saison par saison</h2>
      <h3>Automne et hiver : l'échenillage</h3>
      <p>
        Pour la processionnaire du pin, c'est le moment clé. Les nids sont visibles et les chenilles sont à l'intérieur :
        on coupe les branches porteuses à l'aide d'outils adaptés, avec combinaison, gants, lunettes et masque, puis les
        nids sont détruits. Les nids hauts ou nombreux demandent du matériel et de l'expérience : c'est l'essentiel de
        notre intervention <Link to="/chenilles-processionnaires-creuse">chenilles processionnaires en Creuse</Link>.
      </p>
      <h3>Fin d'hiver : les éco-pièges</h3>
      <p>
        Un collier posé autour du tronc intercepte les chenilles pendant leur descente et les guide vers un sac rempli de
        terre, où elles se retrouvent piégées. Il doit être en place <strong>avant</strong> les premières processions,
        donc généralement dès la fin de l'automne ou le début de l'hiver. Simple et sans produit, il complète très bien
        l'échenillage.
      </p>
      <h3>Été : les pièges à phéromones</h3>
      <p>
        De juin à septembre, les papillons adultes volent et se reproduisent. Des pièges à phéromones capturent les mâles
        et réduisent les pontes de l'année suivante. Ils ne suppriment pas les nids existants, mais servent aussi à
        mesurer la pression dans votre secteur.
      </p>
      <h3>Toute l'année : les mésanges</h3>
      <p>
        La mésange charbonnière est l'un des rares oiseaux à consommer ces chenilles. Installer quelques nichoirs dans le
        jardin, idéalement avant le printemps, est un excellent investissement à long terme.
      </p>
      <h3>Combiner plutôt que choisir</h3>
      <p>
        Aucune méthode ne suffit seule. Dans la plupart des jardins, la combinaison la plus efficace associe échenillage
        en hiver, éco-pièges avant les processions et nichoirs à mésanges. Les pièges à phéromones viennent en renfort
        lorsque les pins sont nombreux ou que la pression est forte dans le voisinage.
      </p>

      <h2>Les erreurs à éviter</h2>
      <ul>
        <li>Brûler un nid dans l'arbre : risque d'incendie et dispersion des poils.</li>
        <li>Écraser une procession au sol ou la balayer : les poils s'envolent.</li>
        <li>Laisser les enfants et les animaux jouer sous un arbre infesté au moment des processions.</li>
        <li>Grimper dans un arbre sans protection ni matériel de sécurité.</li>
      </ul>
      <p>
        L'entretien régulier des arbres limite aussi les mauvaises surprises : une taille bien menée facilite le
        repérage et l'accès aux nids. Voir notre page <Link to="/elagage-creuse">élagage en Creuse</Link>.
      </p>
    </BlogPostLayout>
  );
}
